import {
  Button,
  Checkbox,
  Container,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { FlexBetween, FlexBox } from "components/flex-box";
import { H3 } from "components/Typography";
import { BlogsPayload, FilterData } from "modules/blogs/types";
import React, { FC, useEffect, useMemo, useState } from "react";
import { blogFilters } from "modules/blogs";
import { useRouter } from "next/router";

interface Props {
  open?: boolean;
  isLoading?: boolean;
  categoryFilter?: FilterData;
  dateFilter?: FilterData;
  contentFilter?: FilterData;
  queries?: BlogsPayload;
  onReset?: () => void;
  toggleOpen?: () => void;
  handleSetPayload?: (val: BlogsPayload) => void;
}

const BlogsFilterDrawer: FC<Props> = ({
  open,
  contentFilter,
  isLoading,
  categoryFilter,
  dateFilter,
  queries,
  handleSetPayload,
  toggleOpen,
  onReset,
}) => {
  const { replace } = useRouter();
  const [checkedItems, setCheckedItems] = useState<string[]>([]);

  const isNoContentFilterSet = useMemo(
    () => (!!queries?.content_type?.[0] ? false : true),
    [queries]
  );

  const isNoCategoryFilterSet = useMemo(
    () => (!!queries?.blog_category?.[0] ? false : true),
    [queries]
  );

  const isNoDateFilterSet = useMemo(
    () => (!!queries?.year_published?.[0] ? false : true),
    [queries]
  );

  const handleClick = (val: string, fieldName: string) => {
    let updatedQueries = { ...queries };

    const updateQuery = (queryKey, val) => {
      const splittedFilter = updatedQueries[queryKey]?.split(",");
      if (!updatedQueries.hasOwnProperty(queryKey) && val !== "all") {
        updatedQueries[queryKey] = val;
      } else if (val === "all") {
        updatedQueries[queryKey] = "all";
      } else if (splittedFilter.includes(val)) {
        let updatedContent = splittedFilter.filter((i) => i !== val);
        updatedQueries[queryKey] = updatedContent[0]
          ? updatedContent.join(",")
          : "all";
      } else {
        splittedFilter.unshift(val);
        updatedQueries[queryKey] = splittedFilter.join(",");
      }

      if (updatedQueries[queryKey] === "all") {
        delete updatedQueries[queryKey];
      }

      replace({ query: updatedQueries }, undefined, { shallow: true });
      handleSetPayload({ [queryKey]: updatedQueries[queryKey] });
    };

    switch (fieldName) {
      case blogFilters.category:
        updateQuery(blogFilters.category, val);
        break;
      case blogFilters.date:
        updateQuery(blogFilters.date, val);
        break;
      case blogFilters.contentType:
        updateQuery(blogFilters.contentType, val);
        break;
    }
  };

  useEffect(() => {
    const keys = Object.keys(queries);
    let checkedItems = [];
    if (keys[0]) {
      keys?.forEach((i) =>
        queries[i].split(",")?.forEach((j) => checkedItems.push(j))
      );
    }
    setCheckedItems(checkedItems);
  }, [queries]);

  return (
    <Drawer
      open={open}
      PaperProps={{ sx: { maxWidth: "70vw" } }}
      onClose={toggleOpen}
    >
      <Container sx={{ p: "1em" }}>
        <FlexBetween>
          <H3>Filter</H3>
          <Button onClick={toggleOpen}>
            <CloseIcon sx={{ color: "#C1CDD7" }} />
          </Button>
        </FlexBetween>
      </Container>
      <Divider
        sx={{
          mb: "1em",
          borderBottom: "1.5px solid rgba(125, 135, 156, 0.2)",
        }}
      />
      <Grid container>
        <Grid item xs={12} mb={1}>
          <Container>
            <Button
              disableRipple
              disabled={isLoading}
              endIcon={<RestartAltIcon sx={{ color: "#C1CDD7" }} />}
              onClick={onReset}
              sx={{
                p: 0,
                "&:hover": {
                  background: "transparent",
                },
              }}
            >
              Clear All
            </Button>
          </Container>
        </Grid>
        <Grid item xs={12} mb={1}>
          <Container>
            <H3 sx={{ fontSize: "18px" }}>Content Type</H3>
            <FlexBox flexDirection="column">
              <FormControlLabel
                disabled={isLoading}
                control={
                  <Checkbox
                    onClick={() => handleClick("all", blogFilters.contentType)}
                    checked={isNoContentFilterSet}
                    sx={{
                      color: "rgba(125, 135, 156, 1)",
                      "&.Mui-checked": {
                        color: "#E94560",
                      },
                    }}
                  />
                }
                label={"All"}
                sx={{
                  color: isNoContentFilterSet
                    ? "#E94560"
                    : "rgba(125, 135, 156, 1)",
                }}
              />
              {contentFilter?.options[0] &&
                contentFilter.options.map((i, ind) => (
                  <FormControlLabel
                    key={ind}
                    label={i}
                    disabled={isLoading}
                    control={
                      <Checkbox
                        onClick={() => handleClick(i, blogFilters.contentType)}
                        checked={checkedItems.includes(i)}
                        sx={{
                          color: "rgba(125, 135, 156, 1)",
                          "&.Mui-checked": {
                            color: "#E94560",
                          },
                        }}
                      />
                    }
                    sx={{
                      color: checkedItems.includes(i)
                        ? "#E94560"
                        : "rgba(125, 135, 156, 1)",
                    }}
                  />
                ))}
            </FlexBox>
          </Container>
        </Grid>
        <Grid item xs={12} mb={1}>
          <Container>
            <H3 sx={{ fontSize: "18px" }}>Category</H3>
            <FlexBox flexDirection="column">
              <FormControlLabel
                disabled={isLoading}
                control={
                  <Checkbox
                    onClick={() => handleClick("all", blogFilters.category)}
                    checked={isNoCategoryFilterSet}
                    sx={{
                      color: "rgba(125, 135, 156, 1)",
                      "&.Mui-checked": {
                        color: "#E94560",
                      },
                    }}
                  />
                }
                label={"All"}
                sx={{
                  color: isNoCategoryFilterSet
                    ? "#E94560"
                    : "rgba(125, 135, 156, 1)",
                }}
              />
              {categoryFilter?.options[0] &&
                categoryFilter.options.map((i, ind) => (
                  <FormControlLabel
                    key={ind}
                    label={i}
                    control={
                      <Checkbox
                        onClick={() => handleClick(i, blogFilters.category)}
                        checked={checkedItems.includes(i)}
                        sx={{
                          color: "rgba(125, 135, 156, 1)",
                          "&.Mui-checked": {
                            color: "#E94560",
                          },
                        }}
                      />
                    }
                    sx={{
                      color: checkedItems.includes(i)
                        ? "#E94560"
                        : "rgba(125, 135, 156, 1)",
                    }}
                  />
                ))}
            </FlexBox>
          </Container>
        </Grid>
        <Grid item xs={12} mb={1}>
          <Container>
            <H3 sx={{ fontSize: "18px" }}>Year</H3>
            <FlexBox flexDirection="column">
              <FormControlLabel
                control={
                  <Checkbox
                    onClick={() => handleClick("all", blogFilters.date)}
                    checked={isNoDateFilterSet}
                    sx={{
                      color: "rgba(125, 135, 156, 1)",
                      "&.Mui-checked": {
                        color: "#E94560",
                      },
                    }}
                  />
                }
                label={"All"}
                sx={{
                  color: isNoDateFilterSet
                    ? "#E94560"
                    : "rgba(125, 135, 156, 1)",
                }}
              />
              {dateFilter?.options[0] &&
                dateFilter.options.map((i, ind) => (
                  <FormControlLabel
                    key={ind}
                    label={i}
                    control={
                      <Checkbox
                        onClick={() => handleClick(i, blogFilters.date)}
                        checked={checkedItems.includes(i)}
                        sx={{
                          color: "rgba(125, 135, 156, 1)",
                          "&.Mui-checked": {
                            color: "#E94560",
                          },
                        }}
                      />
                    }
                    sx={{
                      color: checkedItems.includes(i)
                        ? "#E94560"
                        : "rgba(125, 135, 156, 1)",
                    }}
                  />
                ))}
            </FlexBox>
          </Container>
        </Grid>
      </Grid>
    </Drawer>
  );
};

export default BlogsFilterDrawer;
